import React, { lazy, Suspense, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { Chevron2Icon, CloseIcon, WriteNewMsgIcon } from '../../assets';
import { useAppContainerContext } from '../../contexts/AppContainerContext';
import { useChatListContext } from '../../contexts/ChatListContext';
import { MessagesProvider } from '../../contexts/MessagesContext';
import { useSessionContext } from '../../contexts/SessionContext';
import { inMobile, useWindowDimensions } from '../../hooks/useDimensions';
import { useIsDetailsPage } from '../../routes';
import { RoomType, useOrganizationUserRolesQuery } from '../../types/api.graphql';
import { Vehicle } from '../../types/api.graphql';
import { getInitials } from '../../utils/User';
import StyledLinearProgress from '../StyledLinearProgress/StyledLinearProgress';
import {
  AvatarContainer,
  AvatarImage,
  BackButtonContainer,
  BackButtonText,
  BackButtonVehicleHeader,
  ChatListCounter,
  ChatListHeader,
  ChatListTitle,
  ChatListTitleContainer,
  ChatWindow,
  Chevron,
  ChevronIconContainer,
  Container,
  CreateMsgFAB,
  HeaderIcons,
  IconButtonContainer,
  ListHeaderTitle,
  MessagesContainer,
  MessagesList,
  Row,
  RowSubtitle,
  RowTextContainer,
  RowTitle,
  SearchContainer,
  SearchInput,
  StyledSearchIcon,
  StyledVehicleChatPlaceholder,
  VehicleChatsContainer,
  VehicleHeaderText,
} from './ChatList.styles';
import SingleChatWrapper from './SingleChatWrapper';

const RoomsList = lazy(() => import('./RoomsList'));

export type ChatVehicle = Vehicle & { chatIconAccess?: boolean };

interface ChatListProps {
  defaultCollapsed: boolean;
  selectedVehicle?: ChatVehicle;
  mobileLayout?: boolean;
}

const ChatList = ({ defaultCollapsed, selectedVehicle, mobileLayout = false, ...rest }: ChatListProps) => {
  // Dependencies
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { width: windowWidth } = useWindowDimensions();
  const { me } = useSessionContext();
  const { setActualVDPVehicle, actualVDPVehicle } = useAppContainerContext();
  const isVehicleDetailsView = useIsDetailsPage();

  const {
    generalChats,
    vehicleChats,
    unreadGeneralCount,
    activeRoomData,
    setActiveRoomData,
    resetActiveRoomCount,
    unreadTotalCount,
  } = useChatListContext();

  // State
  const [collapsed, setCollapsed] = useState(defaultCollapsed ?? false);
  const [searchTextLocal, setSearchTextLocal] = useState('');
  const [creatingRoom, setCreatingRoom] = useState<boolean>(false);
  const [searchedGeneralChats, setSearchedGeneralChats] = useState<any>(null);
  const [searchedVehicleChats, setSearchVehicleChats] = useState<any>(null);
  const [searchedMembers, setSearchedMembers] = useState<any>(null);

  const { data } = useOrganizationUserRolesQuery({
    skip: !me?.organizations?.[0]?.id,
    variables: {
      organizationId: me?.organizations?.[0]?.id,
    },
  });

  // Handlers
  const onNew = () => {
    setCreatingRoom(true);
  };

  const onCreateMessageGoBack = () => {
    setSearchTextLocal('');
    setCreatingRoom(false);
  };

  const onRoomItemClick = (roomData: any) => {
    setActiveRoomData(roomData);
  };

  const handleTextChange = (e: any) => {
    const text = e.target.value as string;
    // Update search text
    setSearchTextLocal(text);
  };

  const toggle = () => {
    setCollapsed((prev) => !prev);
  };

  const onChatClose = () => {
    setActiveRoomData(undefined);
  };

  // Helpers
  const filterRoomsFromActualVehicle = (chats: any) => {
    return chats?.filter((chat) => chat.vehicle?.id !== actualVDPVehicle?.id);
  };

  const getGeneralChats = () => {
    let generalChatsAux = generalChats;
    if (searchTextLocal !== '' && searchedGeneralChats) {
      generalChatsAux = searchedGeneralChats;
    }
    return shouldDisplayVehicleChats ? filterRoomsFromActualVehicle(generalChatsAux) : generalChatsAux;
  };

  const userIdHasDirectRoom = (userId: string) => {
    return generalChats?.find(
      (chat) => chat.type === RoomType.InOrgDirectRoom && chat.users?.some((user) => user.id === userId),
    );
  };

  const createDirectMessage = (user: any) => {
    setActiveRoomData({
      img: user?.imageUrl,
      name: [user?.firstName, user?.lastName].join(' '),
      id: undefined,
      type: RoomType.InOrgDirectRoom,
      users: [user],
    });
  };

  // Derived State
  const isMobileVehicleView = inMobile(windowWidth) && !!selectedVehicle;

  const shouldDisplayVehicleChats =
    vehicleChats?.length > 0 && selectedVehicle && (isVehicleDetailsView || isMobileVehicleView);

  const generalRoomsProps = {
    searchInput: searchTextLocal,
    generalChats: getGeneralChats(),
    onNew: onNew,
    onRoomClick: onRoomItemClick,
  };

  const vehicleRoomsProps = {
    searchInput: searchTextLocal,
    generalChats: searchTextLocal !== '' && searchedVehicleChats ? searchedVehicleChats : vehicleChats,
    onNew: onNew,
    onRoomClick: onRoomItemClick,
    showHeader: false,
  };

  const membersList = React.useMemo(() => {
    return data?.organization?.userRoles?.filter(
      (userRole) => !userIdHasDirectRoom(userRole?.user.id) && userRole?.user?.id !== me?.id,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [generalChats, data?.organization, me?.id]);

  const membersListValue = searchedMembers ? searchedMembers : membersList;

  // Effects
  useEffect(() => {
    const searchedMembersLocal = membersList?.filter((member) => {
      const memberName = [member?.user.firstName, member?.user.lastName].join(' ');
      return memberName?.toLocaleLowerCase().includes(searchTextLocal.toLocaleLowerCase());
    });
    setSearchedMembers(searchedMembersLocal);
    const searchedGeneralChatsLocal = generalChats?.filter((chat) =>
      chat.name?.toLocaleLowerCase().includes(searchTextLocal.toLocaleLowerCase()),
    );
    const searchedVehicleChatsLocal = vehicleChats?.filter((chat) =>
      chat.name?.toLocaleLowerCase().includes(searchTextLocal.toLocaleLowerCase()),
    );
    setSearchedGeneralChats(searchedGeneralChatsLocal);
    setSearchVehicleChats(searchedVehicleChatsLocal);
  }, [searchTextLocal, membersList, unreadGeneralCount, generalChats, vehicleChats]);

  useEffect((): any => {
    if (selectedVehicle) {
      /**
       TODO: Add new logic when navigating to vehicle detail
       */
    }
  }, [selectedVehicle]);

  useEffect(() => {
    return () => {
      // Only in mobile is necessary to reset the actual VDP vehicle because in other devices is cleaned in the VDP unmount.
      if (inMobile(windowWidth)) {
        setActualVDPVehicle(undefined);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (selectedVehicle?.chatIconAccess) {
      const existingRoom = vehicleChats?.find((chat) => chat.vehicle?.id === selectedVehicle?.id);
      if (existingRoom) {
        onRoomItemClick({ ...existingRoom });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedVehicle, vehicleChats]);

  // Partials
  const ChatComponent = React.useMemo(() => {
    return (
      <ChatWindow>
        <MessagesProvider roomData={activeRoomData} onLoadMessages={resetActiveRoomCount} senderId={me?.id}>
          <SingleChatWrapper
            roomType={activeRoomData?.type}
            onClose={onChatClose}
            roomData={{
              ...activeRoomData,
              receiverId:
                (activeRoomData?.type === RoomType.InOrgDirectRoom &&
                  activeRoomData?.users?.filter((user) => user.id !== me?.id)?.[0]?.id) ||
                (activeRoomData?.type === RoomType.OrgInviteRoom && activeRoomData?.receiverId),
            }}
            vehicleId={activeRoomData?.vehicle?.id}
          />
        </MessagesProvider>
      </ChatWindow>
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeRoomData]);

  const MemberRow = ({ firstName, lastName, imgUrl, handleClick }: any) => {
    return (
      <Row onClick={handleClick}>
        <AvatarContainer>
          <AvatarImage text={getInitials(firstName, lastName)} src={imgUrl} />
        </AvatarContainer>
        <RowTextContainer>
          <RowTitle>{[firstName, lastName].join(' ')}</RowTitle>
          <RowSubtitle>{t('components.chat.writeAMessageTo', { firstName })}</RowSubtitle>
        </RowTextContainer>
      </Row>
    );
  };

  const MobileVehicleHeader = (
    <>
      <BackButtonVehicleHeader onClick={() => navigate(-1)}>
        <Chevron />
      </BackButtonVehicleHeader>
      <AvatarContainer $isHeaderAvatar>
        {selectedVehicle?.pictures?.[0]?.url ? (
          <AvatarImage src={selectedVehicle?.pictures?.[0].url} />
        ) : (
          <StyledVehicleChatPlaceholder />
        )}
      </AvatarContainer>
      <VehicleHeaderText>{selectedVehicle?.displayName}</VehicleHeaderText>
      <IconButtonContainer onClick={() => navigate(-1)}>
        <CloseIcon />
      </IconButtonContainer>
    </>
  );

  const ChatListGeneralContent = React.useMemo(() => {
    return (
      !collapsed &&
      (!creatingRoom ? (
        <>
          <SearchContainer $shouldShow>
            <SearchInput
              id="search"
              label=""
              placeholder={t('components.chat.search')}
              name="search"
              aria-label={t('components.chat.search')}
              clearable
              iconLeft={<StyledSearchIcon />}
              onChange={handleTextChange}
              value={searchTextLocal}
            />
          </SearchContainer>
          <Suspense fallback={<StyledLinearProgress horizontalPadding="1.5rem" />}>
            {shouldDisplayVehicleChats && (
              <VehicleChatsContainer>
                <RoomsList
                  {...vehicleRoomsProps}
                  title={t('components.chat.chatsAboutVehicle', { vehicle: selectedVehicle.displayName })}
                  collapsible
                />
              </VehicleChatsContainer>
            )}
            <RoomsList
              {...generalRoomsProps}
              title={shouldDisplayVehicleChats ? t('components.chat.allChats') : undefined}
            />
          </Suspense>
        </>
      ) : (
        <>
          <BackButtonContainer onClick={onCreateMessageGoBack}>
            <Chevron2Icon />
            <BackButtonText>{t('common.back')}</BackButtonText>
          </BackButtonContainer>
          <SearchContainer $shouldShow>
            <SearchInput
              id="search"
              label=""
              placeholder={t('components.chat.search')}
              name="search"
              aria-label={t('components.chat.search')}
              clearable
              iconLeft={<StyledSearchIcon />}
              onChange={handleTextChange}
              value={searchTextLocal}
            />
          </SearchContainer>
          <MessagesContainer>
            <ListHeaderTitle>Members</ListHeaderTitle>
            <MessagesList>
              {membersListValue?.map((userRole) => (
                <MemberRow
                  firstName={userRole?.user?.firstName}
                  lastName={userRole?.user?.lastName}
                  imgUrl={userRole?.user?.imageUrl}
                  handleClick={() => createDirectMessage(userRole?.user)}
                />
              ))}
            </MessagesList>
          </MessagesContainer>
        </>
      ))
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [collapsed, creatingRoom, vehicleChats, searchedGeneralChats, generalChats]);

  const ChatListVehicleContent = React.useMemo(() => {
    return (
      shouldDisplayVehicleChats && (
        <VehicleChatsContainer $addPaddingTop>
          <>
            <RoomsList {...vehicleRoomsProps} title={t('components.chat.chat')} />
          </>
        </VehicleChatsContainer>
      )
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vehicleChats, selectedVehicle]);

  return (
    <>
      <Container $isCollapsed={collapsed} {...rest} mobileLayout={mobileLayout} $fullScreen={isMobileVehicleView}>
        <ChatListHeader onClick={toggle}>
          {isMobileVehicleView ? (
            MobileVehicleHeader
          ) : (
            <>
              <ChatListTitleContainer>
                <ChatListTitle>{t('components.chat.chat')}</ChatListTitle>
                {collapsed && unreadTotalCount > 0 && <ChatListCounter>{unreadTotalCount}</ChatListCounter>}
              </ChatListTitleContainer>
              {!mobileLayout && (
                <>
                  <IconButtonContainer
                    onClick={(e) => {
                      e.stopPropagation();
                      onNew();
                    }}
                  >
                    <WriteNewMsgIcon />
                  </IconButtonContainer>
                  <HeaderIcons>
                    {/*collapsed && <IconButton icon={<StyledChatWriteIcon />} />*/}
                    <ChevronIconContainer>
                      <Chevron collapsed={collapsed} />
                    </ChevronIconContainer>
                  </HeaderIcons>
                </>
              )}
            </>
          )}
        </ChatListHeader>
        {isMobileVehicleView ? ChatListVehicleContent : ChatListGeneralContent}
        <CreateMsgFAB onClick={onNew} $hide={creatingRoom || isMobileVehicleView}>
          <WriteNewMsgIcon />
        </CreateMsgFAB>
      </Container>
      {activeRoomData && ChatComponent}
    </>
  );
};

export default ChatList;
